/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';
import type { OneTrustConfig } from '@/lib/tracking/onetrust-config.server';
import { registerOneTrustGlobals } from '@/lib/tracking/onetrust';

export interface OneTrustLoaderProps {
    config: OneTrustConfig;
    /** CSP nonce for inline scripts; null/undefined when security headers middleware is disabled. */
    nonce?: string | null;
}

/**
 * Config-driven OneTrust CMP loader — the Storefront Next equivalent of
 * `onetrustcookies.isml`. Renders the same two `<script>` tags as the legacy
 * template (`OtAutoBlock.js` + `otSDKStub.js`, with the conditional
 * `data-ignore-ga` attribute), driven by the shared tracking-config Custom
 * API response instead of `dw.system.Site.getCustomPreferenceValue` calls.
 *
 * Renders nothing when OneTrust isn't enabled/configured for the site.
 */
export function OneTrustLoader({ config, nonce }: OneTrustLoaderProps) {
    // Registers the real OptanonWrapper/openConsentPreferences handlers from
    // src/lib/tracking/onetrust.ts. Runs on every page (not gated on
    // config.onetrustEnable) so openConsentPreferences safely no-ops with a
    // logged event rather than being undefined, per acceptance criteria.
    useEffect(() => {
        registerOneTrustGlobals();
    }, []);

    if (!config.onetrustEnable || !config.onetrustDataDomainId) {
        return null;
    }

    return (
        <>
            <script
                type="text/javascript"
                nonce={nonce ?? undefined}
                src={`https://cdn.cookielaw.org/consent/${config.onetrustDataDomainId}/OtAutoBlock.js`}
            />
            <script
                type="text/javascript"
                charSet="UTF-8"
                nonce={nonce ?? undefined}
                src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
                data-domain-script={config.onetrustDataDomainId}
                {...(config.onetrustPreventGaEvents ? { 'data-ignore-ga': 'true' } : {})}
            />
            {/*
                Defines window.OptanonWrapper and preferences trigger functions synchronously at HTML-parse time —
                mirrors the legacy `function OptanonWrapper() {}` stub, which also
                had to exist before OneTrust could call it. Delegates to
                `__onetrustHandleConsentUpdate`, wired up by the useEffect above
                once the app hydrates; calling OptanonWrapper before that has
                happened is a safe no-op.
                
                Also exposes pre-hydration stubs for openConsentPreferences and
                OptOutDisplayBanner so they are immediately available to CMS-authored
                inline click handlers, and return 'false' to prevent default navigation.
            */}
            <script
                nonce={nonce ?? undefined}
                suppressHydrationWarning
                dangerouslySetInnerHTML={{
                    __html: `window.OptanonWrapper = function () {
                        if (typeof window.__onetrustHandleConsentUpdate === 'function') {
                            window.__onetrustHandleConsentUpdate();
                        }
                    };
                    window.openConsentPreferences = window.OptOutDisplayBanner = function () {
                        if (typeof window.OneTrust !== 'undefined' && window.OneTrust) {
                            window.OneTrust.ToggleInfoDisplay();
                        }
                        return false;
                    };`,
                }}
            />
        </>
    );
}
